import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { Container, Header, Segment, Button, Icon, Item, Message, Divider, Loader, Form, Checkbox, Input, Dropdown } from 'semantic-ui-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { toast } from 'react-toastify';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { clearCart } from '../../reducers/cartSlice';
import ProfileNav from '../CaptainProfileEdit/ProfileNav/ProfileNav';
import Footer from '../Footer/Footer';
import './ProductCheckout.css';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

const US_STATES = [
  { name: 'Alabama', code: 'AL' },
  { name: 'Alaska', code: 'AK' },
  { name: 'American Samoa', code: 'AS' },
  { name: 'Arizona', code: 'AZ' },
  { name: 'Arkansas', code: 'AR' },
  { name: 'California', code: 'CA' },
  { name: 'Colorado', code: 'CO' },
  { name: 'Connecticut', code: 'CT' },
  { name: 'Delaware', code: 'DE' },
  { name: 'District of Columbia', code: 'DC' },
  { name: 'Florida', code: 'FL' },
  { name: 'Georgia', code: 'GA' },
  { name: 'Guam', code: 'GU' },
  { name: 'Hawaii', code: 'HI' },
  { name: 'Idaho', code: 'ID' },
  { name: 'Illinois', code: 'IL' },
  { name: 'Indiana', code: 'IN' },
  { name: 'Iowa', code: 'IA' },
  { name: 'Kansas', code: 'KS' },
  { name: 'Kentucky', code: 'KY' },
  { name: 'Louisiana', code: 'LA' },
  { name: 'Maine', code: 'ME' },
  { name: 'Maryland', code: 'MD' },
  { name: 'Massachusetts', code: 'MA' },
  { name: 'Michigan', code: 'MI' },
  { name: 'Minnesota', code: 'MN' },
  { name: 'Mississippi', code: 'MS' },
  { name: 'Missouri', code: 'MO' },
  { name: 'Montana', code: 'MT' },
  { name: 'Nebraska', code: 'NE' },
  { name: 'Nevada', code: 'NV' },
  { name: 'New Hampshire', code: 'NH' },
  { name: 'New Jersey', code: 'NJ' },
  { name: 'New Mexico', code: 'NM' },
  { name: 'New York', code: 'NY' },
  { name: 'North Carolina', code: 'NC' },
  { name: 'North Dakota', code: 'ND' },
  { name: 'Northern Marianas Islands', code: 'MP' },
  { name: 'Ohio', code: 'OH' },
  { name: 'Oklahoma', code: 'OK' },
  { name: 'Oregon', code: 'OR' },
  { name: 'Pennsylvania', code: 'PA' },
  { name: 'Puerto Rico', code: 'PR' },
  { name: 'Rhode Island', code: 'RI' },
  { name: 'South Carolina', code: 'SC' },
  { name: 'South Dakota', code: 'SD' },
  { name: 'Tennessee', code: 'TN' },
  { name: 'Texas', code: 'TX' },
  { name: 'Utah', code: 'UT' },
  { name: 'Vermont', code: 'VT' },
  { name: 'Virgin Islands', code: 'VI' },
  { name: 'Virginia', code: 'VA' },
  { name: 'Washington', code: 'WA' },
  { name: 'West Virginia', code: 'WV' },
  { name: 'Wisconsin', code: 'WI' },
  { name: 'Wyoming', code: 'WY' },
];

const ProductCheckout = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { items: cartItems } = useSelector((state) => state.cart);
  const currentUser = useSelector((state) => state.currentUser);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    name: '', line1: '', line2: '', city: '', state: '', postal_code: '', country_code: 'US', email: '',
  });
  const [useProfileAddress, setUseProfileAddress] = useState(true);

  const hasShippableItems = useMemo(() => cartItems.some(item => item.shippable), [cartItems]);

  useEffect(() => {
    if (currentUser) {
      const matchingState = US_STATES.find(s => s.name === currentUser.state);
      const stateCode = matchingState ? matchingState.code : (currentUser.state || '');
      setShippingAddress({
        name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
        line1: currentUser.address || '',
        line2: '',
        city: currentUser.city || '',
        state: stateCode,
        postal_code: currentUser.zipCode || '',
        country_code: 'US',
        email: currentUser.email || '',
      });
      setUseProfileAddress(true);
    } else {
      setUseProfileAddress(false);
    }
  }, [currentUser]);

  const handleAddressChange = (e, { name, value }) => {
    setShippingAddress(prev => ({ ...prev, [name]: value }));
  };

  const addressError = useMemo(() => {
    // 1. Guest Email Validation (Always required for guests)
    if (!currentUser && (!shippingAddress.email || !/\S+@\S+\.\S+/.test(shippingAddress.email))) {
      return 'Please enter a valid email address for order updates.';
    }

    // 2. Shipping Validation (Only if shippable items exist)
    if (hasShippableItems) {
      if (!shippingAddress.name || !shippingAddress.line1 || !shippingAddress.city || !shippingAddress.state || !shippingAddress.postal_code) {
        return 'Please fill out all required shipping fields.';
      }
      if (!/^[A-Z]{2}$/i.test(shippingAddress.state)) {
        return 'Please enter a valid 2-letter state code (e.g., FL).';
      }
      if (!/^\d{5}(-\d{4})?$/.test(shippingAddress.postal_code)) {
        return 'Please enter a valid 5-digit zip code.';
      }
    }
    return null;
  }, [hasShippableItems, shippingAddress, currentUser]);

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to empty your cart?')) {
      dispatch(clearCart());
      toast.info('Your cart has been cleared.');
    }
  };

  useEffect(() => {
    if (cartItems.length === 0) {
      setQuoteLoading(false);
      return;
    }

    const fetchQuote = async () => {
      setQuoteLoading(true);
      try {
        const functions = getFunctions();
        const getProductCartQuote = httpsCallable(functions, 'getProductCartQuote');
        const itemsForBackend = cartItems.map(item => ({
          productId: item.id,
          quantity: item.quantity,
        }));
        const result = await getProductCartQuote({ cartItems: itemsForBackend });
        if (result.data.success) {
          setQuote(result.data.quote);
        } else {
          throw new Error(result.data.message || 'Failed to get quote.');
        }
      } catch (error) {
        toast.error(`Could not calculate total: ${error.message}`);
      } finally {
        setQuoteLoading(false);
      }
    };

    fetchQuote();
  }, [cartItems]);

  // This logic determines the correct merchant ID for the PayPal SDK.
  const getPayeeMerchantId = () => {
    if (cartItems.length === 0) return null;

    // Get all unique seller merchant IDs from the cart.
    // Platform-sold items will have a null/undefined ownerPayPalMerchantId.
    const sellerIds = new Set(cartItems.map(item => item.ownerPayPalMerchantId || process.env.REACT_APP_PAYPAL_PLATFORM_MERCHANT_ID));

    // If there's only ONE unique seller (or only platform items), pass the merchant-id.
    if (sellerIds.size === 1) {
      return sellerIds.values().next().value;
    }

    // If there are multiple sellers, do NOT pass a merchant-id.
    // This tells the SDK to expect a multi-seller transaction, matching the backend.
    return null;
  };
  const payeeMerchantId = getPayeeMerchantId();

  const paypalOptions = {
    'client-id': PAYPAL_CLIENT_ID,
    currency: 'USD',
    intent: 'capture',
    ...(payeeMerchantId && { 'merchant-id': payeeMerchantId }),
  };

  const createOrder = async (data, actions) => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty.');
      return Promise.reject(new Error('Empty cart'));
    }

    try {
      const functions = getFunctions();
      const createProductOrder = httpsCallable(functions, 'createProductOrder');
      // Map frontend cart structure to the backend's expected structure.
      const itemsForBackend = cartItems.map(item => ({
        productId: item.id, // Ensure `id` is mapped to `productId`
        quantity: item.quantity,
        price: item.price,
        name: item.name,
      }));
      const result = await createProductOrder({ 
        cartItems: itemsForBackend,
        ...(hasShippableItems && { shippingAddress }),
      });

      if (result.data.orderId) {
        return result.data.orderId;
      }
      throw new Error(result.data.message || 'Could not create PayPal order from backend.');
    } catch (err) {
      toast.error(`Could not initiate checkout: ${err.message}`);
      return Promise.reject(err);
    }
  };

  const onApprove = async (data, actions) => {
    setIsProcessing(true);
    try {
      const functions = getFunctions();
      const captureProductPurchase = httpsCallable(functions, 'captureProductPurchase');
      const result = await captureProductPurchase({
        orderID: data.orderID,
        cartItems: cartItems.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          options: item.selectedOptions,
        })),
        buyerEmail: shippingAddress.email,
        ...(hasShippableItems && { shippingAddress }),
      });

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to capture payment on server.');
      }

      toast.success('Payment successful! Thank you for your purchase.');
      dispatch(clearCart());

      // Redirect logged-in users to their receipt. Guests see the toast and cart clears.
      if (currentUser) {
        history.push(`/receipt/${result.data.purchaseId}`);
      }
    } catch (error) {
      console.error('Error capturing cart payment:', error);
      toast.error(`An error occurred with your purchase: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const onError = (err) => {
    console.error('PayPal Error:', err);
    toast.error('An error occurred with the payment. Please try again.');
    setIsProcessing(false);
  };

  if (cartItems.length === 0 && !isProcessing) {
    return (
      <>
        <ProfileNav />
        <div className="checkout-page-container">
          <Container>
            <Message info icon>
              <Icon name="info circle" />
              <Message.Content>
                <Message.Header>Your Cart is Empty</Message.Header>
                You will be redirected to the marketplace shortly.
              </Message.Content>
            </Message>
          </Container>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      {currentUser && <ProfileNav />}
      <div className="checkout-page-container">
        <Container>
          <Header as="h1" className="checkout-header">Checkout</Header>
          <Segment padded>
            <Button
              basic
              color="red"
              floated="right"
              size="tiny"
              onClick={handleClearCart}
              style={{ marginTop: '-30px', zIndex: 2, position: 'relative' }}
            >
              <Icon name="trash" /> Clear Cart
            </Button>
            <Header as="h3">Order Summary</Header>
            <Item.Group divided>
              {cartItems.map(item => (
                <Item key={item.cartItemId}>
                  <Item.Image src={item.imageUrls?.[0] || 'https://react.semantic-ui.com/images/wireframe/image.png'} size="tiny" />
                  <Item.Content verticalAlign="middle">
                    <Item.Header>{item.name}</Item.Header>
                    <Item.Meta>Qty: {item.quantity} @ ${Number(item.price).toFixed(2)}</Item.Meta>
                  </Item.Content>
                </Item>
              ))}
            </Item.Group>
            <Divider />
            {quoteLoading ? (
              <Loader active inline="centered" />
            ) : quote ? (
              <div className="checkout-totals">
                <p>Subtotal: <span>${quote.subtotal.toFixed(2)}</span></p>
                <p>Shipping: <span>${quote.shipping.toFixed(2)}</span></p>
                {quote.handling > 0 && <p>Handling: <span>${quote.handling.toFixed(2)}</span></p>}
                <p>Tax: <span>${quote.tax.toFixed(2)}</span></p>
                <Divider />
                <Header as="h3">
                  Total: <span>${quote.total.toFixed(2)}</span>
                </Header>
              </div>
            ) : (
              <Message negative>
                <Message.Header>Could not calculate total</Message.Header>
                <p>There was an error calculating your order total. Please try again.</p>
              </Message>
            )}
          </Segment>

          {/* Guest Email Collection */}
          {!currentUser && (
            <Segment padded>
              <Header as="h3">Contact Information</Header>
              <Form>
                <Form.Input label="Email Address" name="email" value={shippingAddress.email} onChange={handleAddressChange} required type="email" placeholder="For order receipt and updates" />
              </Form>
            </Segment>
          )}

          {hasShippableItems && (
            <Segment padded>
              <Header as="h3">Shipping Information</Header>
              <Form>
                {currentUser && (
                  <Form.Field>
                    <Checkbox
                      label="Use my profile address"
                      checked={useProfileAddress}
                      onChange={() => setUseProfileAddress(!useProfileAddress)}
                    />
                  </Form.Field>
                )}
                {!useProfileAddress && (
                  <>
                    <Form.Input label="Full Name" name="name" value={shippingAddress.name} onChange={handleAddressChange} required />
                    <Form.Input label="Address Line 1" name="line1" value={shippingAddress.line1} onChange={handleAddressChange} required />
                    <Form.Input label="Address Line 2" name="line2" value={shippingAddress.line2} onChange={handleAddressChange} />
                    <Form.Group widths="equal">
                      <Form.Input label="City" name="city" value={shippingAddress.city} onChange={handleAddressChange} required />
                      <Form.Field
                        control={Dropdown}
                        label="State"
                        name="state"
                        placeholder="Select State"
                        search
                        selection
                        options={US_STATES.map(s => ({ key: s.code, value: s.code, text: s.name }))}
                        value={shippingAddress.state}
                        onChange={handleAddressChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group widths="equal">
                      <Form.Input label="Postal Code" name="postal_code" value={shippingAddress.postal_code} onChange={handleAddressChange} required />
                      <Form.Input label="Country Code" name="country_code" value={shippingAddress.country_code} onChange={handleAddressChange} required />
                    </Form.Group>
                  </>
                )}
              </Form>
              {addressError && (
                <Message 
                  error 
                  visible
                  size="small" 
                  content={useProfileAddress && currentUser ? `Your profile address is incomplete: ${addressError} Please uncheck 'Use my profile address' to enter manually.` : addressError} 
                  style={{ marginTop: '1em' }}
                />
              )}
            </Segment>
          )}

          <Segment padded>
            <Header as="h3">Payment</Header>
            {isProcessing ? (
              <Message icon info>
                <Icon name="circle notched" loading />
                <Message.Content>
                  <Message.Header>Processing Payment</Message.Header>
                  Please wait while we finalize your transaction. Do not close this window.
                </Message.Content>
              </Message>
            ) : (
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalButtons style={{ layout: 'vertical' }} createOrder={createOrder} onApprove={onApprove} onError={onError} disabled={isProcessing || quoteLoading || !quote || !!addressError} />
              </PayPalScriptProvider>
            )}
          </Segment>
        </Container>
      </div>
      <Footer />
    </>
  );
};

export default ProductCheckout;
